import { ChangeProjectionService, CounterDeltas, Roi, unlabeled } from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface SyncRoiChangesPayload {
  sampleId: string;
  currentAnalystId: string;
}

export interface SyncRoiChangesResult {
  syncedRois: Roi[];
  deletedRois: Roi[];
  counterDeltas: CounterDeltas;
}

export class SyncRoiChangesUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  public async execute({ sampleId, currentAnalystId }: SyncRoiChangesPayload): Promise<SyncRoiChangesResult> {
    const changes = await this.roiChanges.getForSampleAndAnalyst(sampleId, currentAnalystId);

    if (changes.length === 0) {
      return { syncedRois: [], deletedRois: [], counterDeltas: this.emptyCounterDeltas() };
    }

    const serverIds = changes.filter((c) => !c.isNew).map((c) => c.id);
    const fetched = await this.rois.batchGetByIds(serverIds);
    const baseCachedRois = Array.from(fetched.values());

    // Add new ROI snapshots from changes (not in cache since they were never synced)
    const snapshotRois = changes
      .filter((c) => c.isNew && c.snapshot && !baseCachedRois.some((r) => r.id === c.id))
      .map((c) => c.snapshot as Roi);

    const cachedRois = [...baseCachedRois, ...snapshotRois];

    const findingIds = Array.from(new Set(cachedRois.map((r) => r.findingId).filter((id): id is string => !!id)));

    const originalRois = await this.rois.fetchRoisByFindingIds(findingIds);

    // Include cached ROIs that have no findingId (new ROIs not yet on server)
    for (const roi of cachedRois) {
      if (originalRois.has(roi.id))  continue;
      originalRois.set(roi.id, roi);
    }

    const createdRois: Roi[] = [];
    const updatedRois: Roi[] = [];
    const deletedRois: Roi[] = [];
    const changedIds = new Set<string>();

    for (const change of changes) {
      changedIds.add(change.id);

      const originalRoi = originalRois.get(change.id);
      if (!originalRoi) continue;

      const updatedRoi = ChangeProjectionService.applyChanges(originalRoi, change);

      if (change.isNew && updatedRoi.labels.size > 0) {
        createdRois.push(updatedRoi);
      } else if (updatedRoi.labels.size > 0) {
        updatedRois.push(updatedRoi);
      } else {
        deletedRois.push(updatedRoi);
      }
    }

    const affectedFindingIds = new Set([...createdRois, ...updatedRois, ...deletedRois].map((r) => r.findingId));

    const unchangedRois = Array.from(originalRois.values()).filter(
      (r) => !changedIds.has(r.id) && affectedFindingIds.has(r.findingId)
    );

    const syncedRois = await this.rois.updateRois({
      unchangedRois,
      createdRois,
      updatedRois,
      deletedRois,
      roiChanges: changes,
    });

    await this.roiChanges.deleteRoiChanges(Array.from(changedIds));

    return {
      syncedRois,
      deletedRois,
      counterDeltas: this.deletionCounterDeltas(deletedRois.length),
    };
  }

  private emptyCounterDeltas(): CounterDeltas {
    return {
      labelCountDeltas: new Map(),
      singleLabelDeltas: new Map(),
      multiLabelDeltas: new Map(),
    };
  }

  private deletionCounterDeltas(deletedCount: number): CounterDeltas {
    const labelCountDeltas = deletedCount > 0 ? new Map([[unlabeled, -deletedCount]]) : new Map<string, number>();
    return {
      labelCountDeltas,
      singleLabelDeltas: new Map(),
      multiLabelDeltas: new Map(),
    };
  }
}
